// src/features/hmis/components/HMICreate.tsx
import React from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  ReferenceInput,
  SelectInput,
  required,
  useNotify,
  useRedirect,
  useGetIdentity,
  CreateProps,
  useTranslate,
  FormDataConsumer, // Usar FormDataConsumer en lugar de useWatch
} from 'react-admin';
import { Box, Alert } from '@mui/material';

// COMPONENTES DEL SISTEMA
import { MyToolbar } from '../../../components/forms/MyToolbar';
import { MyFormContainer } from '../../../components/forms/MyFormContainer';
import { 
  CenteredFormLayout,
  MyFormSection, 
} from '../../../components/forms/MyFormLayout';

// Hooks y tipos
import { useHMIValidation } from '../hooks/useHMIValidation';
import { useHMIPermissions } from '../hooks/useHMIPermissions';
import { prepareHMIForAPI, HMIFormData, HMI_TYPE_CHOICES } from '../types';

const HMICreate: React.FC<CreateProps> = (props) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const { data: identity } = useGetIdentity();
  const { canCreateHMIs } = useHMIPermissions();
  const {
    validateName,
    validateType,
    validateHotelId,
    validateZoneId,
  } = useHMIValidation();

  // Check permissions
  if (!canCreateHMIs) {
    return (
      <MyFormContainer maxWidth="md">
        <Alert severity="error">
          {translate('resources.hmis.messages.no_permission_create', 'Solo los SuperAdmins pueden crear nuevos HMIs.')}
        </Alert>
      </MyFormContainer>
    );
  }

  const transform = (data: HMIFormData) => {
    return prepareHMIForAPI(data);
  };

  const handleSuccess = (data: any) => {
    // Show initial API key if provided
    if (data.initialApiKey) {
      notify(
        translate('resources.hmis.messages.create_success_with_key'), 
        { 
          type: 'success',
          multiLine: true 
        }
      );
    } else {
      notify(translate('resources.hmis.messages.create_success'), { type: 'success' });
    }
    redirect('list', 'hmis');
  };

  const handleCancel = () => redirect('list', 'hmis');

  return (
    <Create
      {...props}
      title={translate('resources.hmis.titles.create', 'Crear Nuevo HMI')}
      transform={transform}
      mutationOptions={{ onSuccess: handleSuccess }}
    >
      <MyFormContainer >
        <SimpleForm 
          toolbar={
            <MyToolbar 
              onCancel={handleCancel}
              resource="hmis"
              showDelete={false}
            />
          }
        >
          <CenteredFormLayout width={{ xs: 200, sm: 400, md: 600 }}>
            <MyFormSection 
              variant='outlined'
              title={translate('resources.hmis.sections.create', 'Nuevo HMI')}
              
            >
              {/* Información básica del HMI */}
              <Box display="flex" gap={4} width="100%">
                <TextInput
                  source="name"
                  label={translate('resources.hmis.fields.name')}
                  validate={[required(), validateName]}
                  fullWidth
                  size="small"
                />

                <SelectInput
                  source="type"
                  label={translate('resources.hmis.fields.type')}
                  validate={[required(), validateType]}
                  choices={HMI_TYPE_CHOICES}
                  fullWidth
                  size="small"
                />
              </Box>

              {/* Ubicación */}
              <Box display="flex" gap={4} width="100%">
                <ReferenceInput
                  source="hotelId"
                  reference="hotels"
                  label={translate('resources.hmis.fields.hotel')}
                  sort={{ field: 'name', order: 'ASC' }}
                  filter={identity?.role !== 'SuperAdmin' ? { id: identity?.hotelId } : {}}
                >
                  <SelectInput 
                    optionText="name" 
                    fullWidth
                    size="small"
                    disabled={identity?.role !== 'SuperAdmin'}
                    defaultValue={identity?.hotelId}
                    validate={[required(), validateHotelId]}
                  />
                </ReferenceInput>

                <FormDataConsumer>
                  {({ formData }) => (
                    <ReferenceInput
                      source="zoneId"
                      reference="zones"
                      label={translate('resources.hmis.fields.zone')}
                      sort={{ field: 'name', order: 'ASC' }}
                      filter={{ hotelId: formData.hotelId || identity?.hotelId }}
                      key={formData.hotelId}
                    >
                      <SelectInput 
                        optionText="name" 
                        fullWidth
                        size="small"
                        validate={[required(), validateZoneId]}
                        disabled={!formData.hotelId && !identity?.hotelId}
                      />
                    </ReferenceInput>
                  )}
                </FormDataConsumer>
              </Box>
             
            </MyFormSection>
          </CenteredFormLayout>
        </SimpleForm>
      </MyFormContainer>
    </Create>
  );
};

export default HMICreate;